/**
 * Smooth single-series line chart — ported from the design prototype
 * (business-report.js `line()`). A bezier path with a soft gradient fill,
 * absolutely-positioned dots (they stay round despite the stretched SVG,
 * and carry a hover title), and a mono x-axis row.
 *
 * Replaces the Chart.js `<Line>` for the subscriber-trend chart.
 */

export interface LinePoint {
  label: string;
  value: number;
}

const W = 900;
const H = 200;
const PAD = 8;

function bezier(pts: ReadonlyArray<readonly [number, number]>): string {
  let path = `M${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const [px, py] = pts[i - 1];
    const [cx, cy] = pts[i];
    const mx = (px + cx) / 2;
    path += ` C${mx} ${py} ${mx} ${cy} ${cx} ${cy}`;
  }
  return path;
}

export default function SmoothLine({
  data,
  secondary,
  color = 'var(--report-green)',
  format = (v: number) => v.toLocaleString(),
  height = 190,
}: {
  data: LinePoint[];
  /** Optional second series (dashed, no fill/dots) sharing the same scale. */
  secondary?: number[];
  color?: string;
  format?: (v: number) => string;
  height?: number;
}) {
  if (data.length < 2) return null;
  const max =
    Math.max(...data.map((d) => d.value), ...(secondary ?? [])) * 1.1 || 1;
  const x = (i: number) => PAD + (i * (W - PAD * 2)) / (data.length - 1);
  const y = (v: number) => H - (v / max) * (H - 14);
  const pts = data.map((d, i) => [x(i), y(d.value)] as const);
  const path = bezier(pts);
  const secondaryPath =
    secondary && secondary.length === data.length
      ? bezier(secondary.map((v, i) => [x(i), y(v)] as const))
      : null;

  return (
    <div className="relative" role="img" aria-label="Line chart">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block w-full" style={{ height }}>
        <defs>
          <linearGradient id="report-line-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={color} stopOpacity=".18" />
            <stop offset="1" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${path} L${W - PAD} ${H} L${PAD} ${H} Z`} fill="url(#report-line-fill)" />
        <path d={path} fill="none" stroke={color} strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
        {secondaryPath && (
          <path
            d={secondaryPath}
            fill="none"
            stroke={color}
            strokeOpacity=".5"
            strokeWidth="2"
            strokeDasharray="5 3"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      <div className="absolute inset-x-0 top-0" style={{ height }}>
        {data.map((d, i) => (
          <span
            key={`${d.label}-${i}`}
            title={format(d.value)}
            className="absolute -ml-[3.5px] -mt-[3.5px] size-[7px] rounded-full border-2 bg-card"
            style={{
              left: `${((x(i) / W) * 100).toFixed(2)}%`,
              bottom: `${(((H - y(d.value)) / H) * 100).toFixed(2)}%`,
              borderColor: color,
            }}
          />
        ))}
      </div>
      <div className="grid grid-flow-col auto-cols-fr pt-2 font-mono text-[11px] text-muted-foreground/80">
        {data.map((d, i) => (
          <span
            key={`${d.label}-${i}`}
            className={i === 0 ? 'text-left' : i === data.length - 1 ? 'text-right' : 'text-center'}
          >
            {d.label}
          </span>
        ))}
      </div>
    </div>
  );
}
